import { Box, Button, MenuItem, Paper, Typography } from '@mui/material'
import AgendaView from './View'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { ReactComponent as Boxes } from '../../assets/icons/boxes.svg'
import { useStyles } from './styles'
import ModalLayout from '../../ui/modals/layout'
import useTranslations from '../../i18n/useTranslations'
import { useCallback, useEffect, useState } from 'react'
import AgendaTable from './TableAgenda/View'

import { useUserContext } from '../../auth/UserProvider'
import Loader from '../../ui/views/Loader'
import SearchInput from '../ShippingsTracking/SearchInput'
import { format } from 'date-fns'
import Select from '../../ui/components/select'
import {
  useAddressBookByCenter,
  useAddressBookSearch,
  useDeleteAddressMutation,
} from '../../services/addressBook'
import AddEditDirection from './AddEditDirection'
import AgendaTypeSwitcherView, {
  TRACKING_OPTIONS,
} from './AgendaTypeSwitcherView'
import ImportedContactsTable from './TableImportedContacts/View'
import FileUploadModal from './FileUploadModal'
import SuccessfulImportedModal from './SuccessfulImportedModal'
import { ReactComponent as Upload } from '../../assets/icons/upload-white.svg'
import { ReactComponent as Plus } from '../../assets/icons/plus.svg'

const useSelectableContact = contacts => {
  const [selected, _setSelected] = useState([])

  const toggleSelection = useCallback(
    contactsInfo => {
      if (!!selected.find(x => x?.id === contactsInfo?.id)) {
        _setSelected(selectedContacts => [
          ...selectedContacts.filter(
            selectedShipping => selectedShipping?.id !== contactsInfo?.id,
          ),
        ])
      } else {
        _setSelected(selectedContacts => [
          ...selectedContacts,
          { ...contactsInfo },
        ])
      }
    },
    [selected],
  )

  const toggleAll = useCallback(
    isChecked => {
      if (isChecked) {
        const nextSelected = contacts.map(({ data }) => {
          return { ...data }
        })
        const difference = selected.filter(
          contact => !nextSelected.some(({ id }) => id === contact?.id),
        )
        _setSelected([...difference])
      } else {
        const nextSelected = contacts.map(({ data }) => {
          return { ...data }
        })
        const difference = nextSelected.filter(contact =>
          selected.filter(({ id }) => id !== contact?.id),
        )
        _setSelected([...difference, ...selected])
      }
    },
    [contacts, selected],
  )

  const clearSelection = useCallback(() => {
    _setSelected([])
  }, [])

  return { selected, toggleSelection, toggleAll, clearSelection }
}

const EmptyState = ({ emptySearch }) => {
  const classes = useStyles()
  const t = useTranslations()
  return (
    <Paper className={clsx(classes.paper, classes.emptyState)}>
      {emptySearch ? (
        <>
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('empty:no-results')}
          </Typography>
          <Typography>
            <span className={classes.secondaryText}>
              {t('empty:no-results-search')}
            </span>
          </Typography>
        </>
      ) : (
        <>
          <Boxes />
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('agenda:empty-state')}
          </Typography>
        </>
      )}
    </Paper>
  )
}

const Agenda = () => {
  const t = useTranslations()
  const classes = useStyles()
  const [centerSelected, setCenterSelected] = useState(null)
  const [addressToDelete, setAddressToDelete] = useState()
  const [addEditAddress, setAddEditAddress] = useState()
  const [filesToUpload, setFilesToUpload] = useState([])
  const [uploadFileModal, setUploadFileModal] = useState(false)
  const [selectedTracking, setSelectedTracking] = useState(
    TRACKING_OPTIONS.agenda,
  )
  const [manifestStatus, setManifestStatus] = useState()

  const importedData = [
    {
      file_id: '661404afd03a83afb00b99e0',
      upload_datetime: '2024-04-08T14:52:31.000000Z',
      file_name: 'Grabación Masiva Plantilla - prueba marialu 8 abril.csv',
      file_size: 1907,
      file_extension: 'csv',
      md5: '5486b4a406b40ff4e074321bb71d04a4',
      compression_type: 'None',
      initial_pickup_details: {
        has_pickup_asap: false,
        is_fixed_pickup: null,
        comments: 'Usar recogida fija',
        date: '2024-04-08',
        time: [],
      },
      manifested_by: 'ruben.diego.cttexpress@gmail.com',
      status: 'PROCESSED',
      total_lines: 1,
      error_lines: 0,
      start_processing_datetime: '2024-04-08T14:52:33.300000Z',
      finish_processing_datetime: '2024-04-08T14:52:37.302000Z',
      data_file: {
        resource_provider: 'S3',
        resource_id:
          '90132/in/shippings/history/2024/04/08/0fb7c62d-620a-4d7a-b79f-792256bbba0a.pdf.csv',
      },
      error_file: {
        resource_provider: null,
        resource_id: null,
      },
    },
  ]

  const [showAlert, setShowAlert] = useState()
  const [tableParams, setTableParams] = useState({
    search: '',
    page: 0,
    rowsPerPage: 10,
    totalRows: 28,
    dateFrom: format(
      new Date(Date.now()).setMonth(new Date().getMonth() - 3),
      'yyyy-MM-dd',
    ),
    dateTo: format(
      new Date(Date.now()).setDate(new Date().getDate() + 1),
      'yyyy-MM-dd',
    ),
  })

  const { centres } = useUserContext()

  const addressesQuery = useAddressBookByCenter(
    {
      centreCode: centerSelected?.id,
      pageLimit: tableParams.rowsPerPage,
      pageOffset: tableParams.page,
    },
    { enabled: tableParams.search === '' },
  )

  const addressesSearchQuery = useAddressBookSearch({
    centreCode: centerSelected?.id,
    pageLimit: tableParams.rowsPerPage,
    pageOffset: tableParams.page,
    searchFilterValue: tableParams.search,
  })

  const query =
    tableParams.search === '' ? addressesQuery : addressesSearchQuery
  const { data: addresses, isFetching: isLoadingContacts } = query ?? []
  const deleteAddressMutation = useDeleteAddressMutation(query.refetch)

  const handleSubmitDelete = clientAddressCode => {
    deleteAddressMutation.mutate({
      clientCenterCode: centerSelected?.id,
      clientAddressCode: clientAddressCode,
    })
    setShowAlert({
      text: t('action:delete-address-my-agenda-body-success'),
      severity: 'success',
    })
  }

  const {
    selected: selectedContactInfo,
    toggleSelection: toggleContactSelection,
    toggleAll: toggleAllContacts,
    clearSelection: clearContactSelection,
  } = useSelectableContact(addresses?.data)

  const resetVariables = () => {
    setShowAlert(null)
    setUploadFileModal(false)
    setManifestStatus()
    setShowAlert(null)
    setFilesToUpload([])
  }

  useEffect(() => {
    setTableParams({
      ...tableParams,
      totalRows: addresses?.pagination?.totalCount,
    })
  }, [addresses])

  useEffect(() => {
    let timer
    if (showAlert !== null) {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 5000)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  useEffect(() => {
    if (centres?.length) setCenterSelected(centres[0])
  }, [centres])

  const renderDeleteModal = () => {
    return (
      <ModalLayout
        isOpen={addressToDelete}
        title={t('action:delete-address-my-agenda-header')}
        content={t('action:delete-address-my-agenda-body')}
        actions={
          <>
            {!addressToDelete?.delete ? (
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={() => setAddressToDelete(null)}
              >
                {t('action:cancel')}
              </Button>
            ) : null}
            <Button
              size="small"
              onClick={() => {
                if (!addressToDelete?.delete)
                  handleSubmitDelete(addressToDelete)
                setAddressToDelete(null)
              }}
            >
              {t('action:confirm')}
            </Button>
          </>
        }
      />
    )
  }

  const renderAddEditModal = () => {
    return (
      <ModalLayout
        isOpen={addEditAddress}
        title={
          addEditAddress?.id
            ? t('agenda:edit-contact')
            : t('agenda:add-contact')
        }
        wide={500}
        content={
          <AddEditDirection
            onSuccessAddContact={() => {
              setAddEditAddress(null)
              setShowAlert({
                severity: 'success',
                text: t('agenda:add-success'),
              })
              query.refetch()
            }}
            onErrorAddContact={() => {
              setAddEditAddress(null)
              setShowAlert({ severity: 'error', text: t('agenda:add-error') })
            }}
            onSuccessEditContact={() => {
              setAddEditAddress(null)
              setShowAlert({
                severity: 'success',
                text: t('agenda:edit-success'),
              })
              query.refetch()
            }}
            onErrorEditContact={() => {
              setAddEditAddress(null)
              setShowAlert({ severity: 'error', text: t('agenda:edit-error') })
            }}
            onCancel={() => setAddEditAddress(null)}
            addEditAddress={addEditAddress}
            clientCenterCode={centerSelected?.id}
          />
        }
      />
    )
  }

  const renderImportModal = () => {
    return !manifestStatus ? (
      <ModalLayout
        onClose={() => {
          setFilesToUpload([])
          setUploadFileModal(false)
        }}
        isOpen={uploadFileModal}
        wide={true}
        content={
          <Box className={classes.modalContainer}>
            <FileUploadModal
              handleRemoveFile={() => {}}
              handleAddFile={() => {}}
              openModal={setUploadFileModal}
              filesToUpload={filesToUpload}
              manifestFiles={() => {}}
            />
          </Box>
        }
        title={t('action:import-contacts')}
      />
    ) : (
      <SuccessfulImportedModal
        isOpen={manifestStatus === 'success'}
        onClose={resetVariables}
      />
    )
  }

  //REFETCH IMPORTED

  return (
    <>
      <AgendaView
        showAlert={showAlert}
        onClickAddContact={() => setAddEditAddress({})}
        onClickImport={() => setUploadFileModal(true)}
      >
        {renderImportModal()}
        {renderDeleteModal()}
        {renderAddEditModal()}

        <Box className={classes.rowInputs}>
          {true ? (
            <Select
              label={t('agenda:select-center')}
              className={classes.centerSelect}
              value={centerSelected}
              onChange={event => {
                setCenterSelected(event.target.value)
              }}
            >
              {centres.map(e => {
                return (
                  <MenuItem key={e} value={e}>
                    <em>{`${e?.id} | ${e?.name}`}</em>
                  </MenuItem>
                )
              })}
            </Select>
          ) : null}
          <Box className={classes.rowButtons}>
            {addresses?.data?.length || tableParams.search?.length ? (
              <SearchInput
                className={classes.searchInput}
                queryValue={tableParams.search}
                noHelperText={true}
                placeholder={t(
                  'placeholder:search-by-name-or-address-or-company',
                )}
                search={value => {
                  setTableParams({ ...tableParams, search: value })
                }}
                clearSearch={() => {
                  setTableParams({ ...tableParams, search: '' })
                }}
              />
            ) : null}

            <Button
              onClick={() => setUploadFileModal(true)}
              size={'small'}
              startIcon={<Upload />}
              className={classes.actionButton}
            >
              {t('agenda:import-contacts')}
            </Button>
            <Button
              onClick={() => setAddEditAddress(true)}
              size={'small'}
              startIcon={<Plus />}
              className={classes.actionButton}
            >
              {t('agenda:add-contact')}
            </Button>
          </Box>
        </Box>

        {isLoadingContacts || !addresses?.data ? (
          <Loader />
        ) : addresses?.data?.length ? (
          <AgendaTable
            showCheckbox={false}
            showMenu={true}
            onClickDeleteContact={contactInfo =>
              setAddressToDelete(contactInfo?.id)
            }
            rows={addresses?.data ?? []}
            handleSubmitDelete={() => {}}
            handleSubmitGetDetail={() => {}}
            tableParams={tableParams}
            onClickEditContact={setAddEditAddress}
            onChangeTableParams={setTableParams}
            onCheckboxChange={toggleContactSelection}
            onSelectAllClick={toggleAllContacts}
            selectedRows={selectedContactInfo}
          />
        ) : (
          <EmptyState
            emptySearch={tableParams.search?.length}
          />
        )}

        {/*REACTIVAR IMPORTACION */}
        {/* <AgendaTypeSwitcherView
          changeSelectedTracking={setSelectedTracking}
          selectedTracking={selectedTracking}
          incidentsCount={10}
          unscheduledPickupsCount={10}
        >
          {isLoadingContacts || !addresses?.data ? (
            <Loader />
          ) : addresses?.data?.length &&
            selectedTracking === TRACKING_OPTIONS.agenda ? (
            <AgendaTable
              showCheckbox={false}
              showMenu={true}
              onClickDeleteContact={contactInfo =>
                setAddressToDelete(contactInfo?.id)
              }
              rows={addresses?.data ?? []}
              handleSubmitDelete={() => {}}
              handleSubmitGetDetail={() => {}}
              tableParams={tableParams}
              onClickEditContact={setAddEditAddress}
              onChangeTableParams={setTableParams}
              onCheckboxChange={toggleContactSelection}
              onSelectAllClick={toggleAllContacts}
              selectedRows={selectedContactInfo}
            />
          ) : selectedTracking === TRACKING_OPTIONS.import ? (
            <ImportedContactsTable
              rows={importedData ?? []}
              handleSubmitDelete={() => {}}
              handleSubmitGetDetail={() => {}}
              tableParams={tableParams}
              onChangeTableParams={setTableParams}
            />
          ) : (
            <EmptyState
              emptySearch={tableParams.search?.length}
              onClickImport={() => console.log(2)}
            />
          )}
        </AgendaTypeSwitcherView>*/}
      </AgendaView>
    </>
  )
}

Agenda.propTypes = {}
EmptyState.propTypes = {
  onClickImport: PropTypes.func,
  emptySearch: PropTypes.bool,
}

export default Agenda
